// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { NoteList } from '@/note/components/note-list';
import { getProjectNotes } from '@/project/api/get-project-notes';
import { Box, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

interface Props {
  /**
   * The project key
   */
  projectKey: string;

  /**
   * Callback when notes are updated
   */
  onNotesUpdated?: () => void;
}

/**
 * A component that displays notes for a specific project.
 * Handles fetching and managing project notes.
 */
export function ProjectNoteList(props: Props) {
  // Fetch project notes using React Query
  const {
    data: notesResponse,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['project', props.projectKey, 'notes'],
    queryFn: () => getProjectNotes(props.projectKey),
  });

  // Handle note updates by either calling the parent callback or refetching
  const handleNotesUpdated = () => {
    if (props.onNotesUpdated) {
      props.onNotesUpdated();
    } else {
      refetch();
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <NoteList
      notes={notesResponse?.data ?? []}
      onNotesUpdated={handleNotesUpdated}
    />
  );
}
